import axios from 'axios';
import { setColourwayStamp } from './colourwayPaint';
import { createDocEntryStore } from './docStore';
import { apiUrl } from '../../apiBase';
import { readState, writeState } from './docState';
import { bumpProductRuns } from './productRuns';
import type { MaterialPair } from './variantLabel';
// Called, never read at load: materialSurface reads this file.
import { surfaceKey } from './materialSurface';
import type { MaterialTexture, StandardSurface } from './standardMaterials';
import type { Rgb } from './sampleColour';

// WHAT A PRODUCT IS MADE OF — Phase 5's first subphase, and an answer about the PRODUCT rather
// than about any one 3D run.
//
// WHY IT MOVED. These numbers used to be the first half of the written engine's answer: the
// same call that wrote the bpy opened by stating MATERIAL lines, and the colours were therefore
// a property of whichever run happened to be pressed. Two builds of one chair came out two
// browns, a re-run for a shape correction re-judged the fabric, and the measured texture could
// only be found by walking the runs of that product looking for the newest one that had any.
// None of that is about 3D at all — it is what the thing is made of.
//
// So it is read once, kept here, and handed to every build (see generate_written, which takes
// `materials` in the request). A shape re-run changes the shape.
//
// PER SUBMODEL, keyed the way a whole-product run is keyed (see productMarkOf): a beige chair
// and a grey one are two products photographed separately, and one answer covering both would
// be one colourway deciding for the other. '' is the submodel of a notice with one listing,
// which is a real answer and not a missing one.

// One material, as a MATERIAL line states it — the shape the backend parses and the shape it
// takes back in a generate request (see openai_blender.clean_materials).
// ALPHABETICAL, wherever materials are listed: the order a call names them in is its own, and
// two readings of one chair drawn in two orders read as two different answers.
export const byName = <T extends { name: string }>(list: T[]): T[] =>
  [...list].sort((a, b) => a.name.localeCompare(b.name));

export type Material = {
  name: string;
  rgb: [number, number, number];
  roughness: number;
  metallic: number;
  // Whether it is made of fibres you could see one of — felt, fabric: the reading's FIBROUS lines.
  fibre?: boolean;
  // WHICH OF THE TWO SURFACES ITS MAP IS DRAWN AS: its PATTERN line. Named, not measured.
  pattern?: Pattern;
  // WHAT IT WAS MEASURED TO LOOK LIKE, where the patches have been measured — the numbers a
  // procedural grain is built from (see glb.grained). Carried WITH the material because that
  // is what it is a fact about.
  texture?: { contrast?: number; chroma?: number; anisotropy?: number; periodMm?: number; pattern?: Pattern };
  // WHICH PART OF THE OBJECT IT IS, on a build handed several for one tool ("blade").
  of?: string;
};

// A grain runs one way, like wood; a mottle is blotches with no direction, like felt.
export type Pattern = 'grain' | 'mottle';

// WHERE A MATERIAL WAS MEASURED IN A PHOTOGRAPH — one of Phase 2's crops of a part the reading put
// on it, read inside the part's outline (see measure_materials). Readings from before carry the
// rectangles their own patches call found, with no part.
export type TextureCrop = {
  imageId: number;
  material: string;
  bbox: [number, number, number, number];
  // The part this crop is of — the patches are Phase 2's crops of the parts made of it.
  part?: string;
};

// ONE CLOSE-UP, as the materials press sends it: which part (its number in the parts list, from
// 1, and its name), the photograph it was cut from and the rectangle of it that was cut (0..1),
// the outlines tracing the part in that photograph, and the part's longest real side — the scale.
// The call draws a clean area on it (a PATCH), and that area, mapped back through `cut`, is what
// is measured.
export type CloseUp = {
  part: number;
  label: string;
  imageId: number;
  cut: [number, number, number, number];
  outlines: [number, number][][];
  partMm?: number;
};

// WHAT EACH MATERIAL WAS MEASURED TO BE, pooled over its crops — see texture_stats, which is
// where each number comes from and what it is worth. A texture is a distribution, not a
// picture: these are it.
export type TextureStat = {
  material: string;
  // The colour as it was READ — linear, shading divided out, highlights rejected, median across
  // the pictures. Not the colour the model stated, which is the row above it in the table.
  rgb: [number, number, number];
  // How much the luminance varies once the shading is gone, over its own mean: what separates
  // wood from a wood-coloured surface.
  contrast?: number;
  // And the colour's own spread at constant brightness.
  chroma?: number;
  // How directional it is, 0..1 — a plank against a felt pad.
  anisotropy?: number;
  // How often the grain repeats: in pixels of the photograph always, in millimetres where the
  // object could be boxed and Phase 2 read a width.
  periodPx?: number;
  periodMm?: number;
  // How many crops answered, and the worst of their kept-pixel fractions — a number nobody can
  // weigh is a number that gets believed.
  from: number;
  worst?: number;
};

// ONE READING OF WHAT THE PRODUCT IS MADE OF. `at` is when it was read, so the list can say
// how old each one is; the model and the reasoning are what every other call in the app keeps
// for its badge.
//
// AN IDENTITY, because there are several: the same chair read twice gives two answers and the
// interesting question is which of them is right. An id rather than the instant, so two reads
// cannot collide on one and the toggle cannot switch to the wrong one.
export type MaterialsAnswer = {
  id: string;
  materials: Material[];
  crops: TextureCrop[];
  stats: TextureStat[];
  model?: string;
  reasoning?: string;
  said?: string;
  at?: string;
  // AND WHAT THE LAST APPLY WROTE onto the meshes for each material — see Textured. Absent
  // until this reading has been applied, which is the honest difference between a number that
  // was measured and a number that is on a file.
  wrote?: Record<string, Textured>;
  // AND WHICH MATERIAL EACH COMPONENT IS, as this reading said — by part index, over the parts
  // it was asked about. HELD, NOT WRITTEN: the parts take it when the reading is applied (see
  // the panel's apply), so a press changes nothing the meshes wear. Absent on a reading made
  // before a reading carried its picks, whose apply leaves the parts as they are.
  partPicks?: PartPicks;
  // AND THE HARDWARE'S HALF, from the same press: the default materials' surfaces and which of
  // them each piece of hardware is. Held too, and written by the apply (see applyHardware).
  hardware?: HardwareReading;
};

// ONE DEFAULT MATERIAL AS THE HARDWARE CALL ANSWERED IT: the one it was asked about (its words
// and colour, as they stood) and what it said of it — absent where it said nothing, the colour
// included (`colour`, the answer's; `rgb` is the list's at the time).
export type HardwareMaterial = { pair: MaterialPair; rgb: Rgb; colour?: Rgb; texture?: MaterialTexture }
  & Partial<StandardSurface>;
export type HardwareReading = {
  materials: HardwareMaterial[];
  // Which parts were asked about, by index, and the material it put each on — a tool made of
  // two is in `picks` twice.
  asked: number[];
  // `of`: which part of the tool that material is ("blade", "handle").
  picks: { at: number; pair: MaterialPair; of?: string }[];
  reasoning: string;
  raw: string;
  // WRITTEN ONTO THE STANDARD MATERIALS AND THE HARDWARE, by its own button (see the panel's
  // applyHw). Its own flag, not the reading's `applied` (Louis 2026-11-03): applying the
  // product's colours marked the whole reading applied, and the hardware's answer, never
  // written, vanished from the table behind the list's old values.
  applied?: boolean;
};

// Which part of a tool each of a reading's picks is, by the material's key — see PartNames.
export const namesOf = (picks: { pair: MaterialPair; of?: string }[]): Record<string, string> =>
  Object.fromEntries(picks.flatMap((one) => (one.of ? [[surfaceKey(one.pair), one.of]] : [])));

export type PartPicks = { asked: number[]; picks: Record<number, MaterialPair> };

// EVERY READING OF ONE SUBMODEL, AND WHICH ONE IS ON THE MESHES.
//
// `applied` is not a preference: it is a claim about bytes on disk. Selecting a reading paints
// it onto every mesh this submodel's product has (see applyMaterials and the apply route), so
// exactly one reading is what the models wear, and the toggle is how that is changed.
type Kept = { reads: MaterialsAnswer[]; applied?: string };

type Saved = Record<string, Kept>;

const KEY = (docId: string) => `notice.materials.${docId}`;

const EMPTY: MaterialsAnswer = { id: '', materials: [], crops: [], stats: [] };

// HOW MANY READINGS ARE KEPT. Each holds a material list, eight rectangles, their measurements
// and a page of reasoning, and they all live in this document's state — which is sent whole on
// every hydrate. A dozen is more comparisons than anybody makes; past that the oldest go, and
// never the applied one, which is the only one that is also a fact about a file.
const MOST = 12;

// ONE SUBMODEL'S READINGS, oldest first, from either shape this key has held: a bare answer
// (one reading, before there was a list) or the list. Migrated on read rather than in a pass
// over every document — the old shape is a complete answer, it just has no id and no siblings.
const keptOf = (value: unknown): Kept => {
  if (!value || typeof value !== 'object') return { reads: [] };
  const said = value as Partial<Kept> & Partial<MaterialsAnswer>;
  if (Array.isArray(said.reads)) {
    return { reads: said.reads.filter((one) => !!one?.id), applied: said.applied };
  }
  if (!Array.isArray(said.materials) || !said.materials.length) return { reads: [] };
  const one: MaterialsAnswer = {
    id: 'first',
    materials: said.materials,
    crops: said.crops ?? [],
    stats: said.stats ?? [],
    model: said.model,
    reasoning: said.reasoning,
    said: said.said,
    at: said.at,
  };
  return { reads: [one], applied: one.id };
};

// Which materials reading this submodel's meshes wear, if one was applied — for a change that
// has to put it back on (see productPieces).
export const appliedReading = (docId: string, submodel: string): string | undefined =>
  load(docId)[submodel]?.applied;

const load = (docId: string): Saved => {
  const raw = readState<Record<string, unknown>>(docId, KEY(docId), {});
  const out: Saved = {};
  for (const [submodel, value] of Object.entries(raw)) out[submodel] = keptOf(value);
  return out;
};

// Which calls are out, per submodel — two of them, and they are separately pressable: reading
// the materials is one call to OpenAI, finding their patches is one Astra call per photograph.
type Snapshot = {
  saved: Saved;
  reading: Set<string>;
  cropping: Set<string>;
  // AND WHICH ARE BEING WRITTEN ONTO THE MESHES — a press ends with one, and so does every
  // press of the toggle.
  applying: Set<string>;
  // AND WHICH PARTS' OWN MESHES that write includes, by catalog item — the part viewers that
  // spin with it. The product's meshes are always in it; a part's only when it is being painted.
  applyingItems: Set<number>;
  // WHEN THE PRESS BEGAN, per submodel — the whole press, spanning both of its calls, so the
  // clock on the button does not restart between them.
  //
  // HERE AND NOT IN THE BUTTON. The subphase switcher unmounts this panel, and a press takes
  // minutes: a hook that starts counting when a component first sees `busy` would call a
  // three-minute read twelve seconds because somebody looked at the meshes card and came back.
  // See useSince, which exists for a start the RUN owns.
  startedAt: Record<string, number>;
  // AND THE APPLY'S OWN CLOCK, for the same reason and not for the reason I gave it: "this is
  // over before anybody has gone anywhere" is not true of a wardrobe's worth of meshes, and it
  // was not even true of a chair — toggling to the models card and back reset it to 0:00.
  // `applyTook` outlives the press, so the total is still there when you come back to it.
  applyAt: Record<string, number>;
  applyTook: Record<string, number>;
};

const store = createDocEntryStore<Snapshot>((docId) => ({
  snapshot: {
    saved: load(docId), reading: new Set(), cropping: new Set(), applying: new Set(),
    applyingItems: new Set(),
    startedAt: {}, applyAt: {}, applyTook: {},
  },
}));

export const useMaterialsRun = store.useSnapshot;

// WHICH READING IS APPLIED — that one or nothing, with no fallback anywhere.
//
// It fell back to the newest, on the argument that a build has to be given numbers. But that
// made "applied" mean two things: a claim about bytes in ten files, and a guess the page made
// on somebody's behalf. A build with no applied reading then quietly used a reading nobody had
// chosen, and the four meshes it sat beside were wearing something else.
//
// So nothing falls back. A product with no applied reading has no materials, which subphase 2
// says out loud instead of building around it.
const appliedIn = (kept: Kept | undefined): MaterialsAnswer =>
  kept?.reads.find((one) => one.id === kept.applied) ?? EMPTY;

// THE READING A BUILD IS HANDED, outside a render — what a run reads on its way out, which is
// not a subscription.
export const materialsFor = (docId: string, submodel: string): MaterialsAnswer =>
  appliedIn(load(docId)[submodel]);

export const answerOf = (snap: Snapshot, submodel: string): MaterialsAnswer =>
  appliedIn(snap.saved[submodel]);

// EVERY READING OF THIS SUBMODEL, NEWEST FIRST — the history the list walks.
export const readsOf = (snap: Snapshot, submodel: string): MaterialsAnswer[] =>
  [...(snap.saved[submodel]?.reads ?? [])].reverse();

// WHICH READING IS APPLIED, by id, or nothing — for the tick on a row and for the two places
// that must not draw a 3D model without one.
export const appliedIdOf = (snap: Snapshot, submodel: string): string | undefined =>
  appliedIn(snap.saved[submodel]).id || undefined;


// When this submodel's press began, or null — for useSince, which turns it into seconds.
export const startedOf = (snap: Snapshot, submodel: string): number | null =>
  snap.startedAt[submodel] ?? null;

// And the apply's: when it began, and what the last one took in seconds.
export const applyStartedOf = (snap: Snapshot, submodel: string): number | null =>
  snap.applyAt[submodel] ?? null;

export const applyTookOf = (snap: Snapshot, submodel: string): number | null =>
  snap.applyTook[submodel] ?? null;

const write = (docId: string, saved: Saved) => {
  writeState(docId, KEY(docId), saved);
  store.patch(docId, { saved });
};

// ADD OR CHANGE ONE READING, by its id. The two calls of a press both land here — the first
// makes the reading, the second measures the patches into the same one — so a press is one
// entry in the list and not two.
const keep = (docId: string, submodel: string, id: string, patch: Partial<MaterialsAnswer>) => {
  const saved = { ...load(docId) };
  const kept = saved[submodel] ?? { reads: [] };
  const reads = [...kept.reads];
  const at = reads.findIndex((one) => one.id === id);
  if (at < 0) reads.push({ ...EMPTY, id, ...patch });
  else reads[at] = { ...reads[at], ...patch };
  // The oldest go first, and never the applied one — it is the only reading that is also a
  // statement about what is on disk.
  const applied = kept.applied ?? id;
  const spare = reads.filter((one) => one.id !== applied);
  while (reads.length > MOST && spare.length) {
    const gone = spare.shift();
    reads.splice(reads.findIndex((one) => one.id === gone?.id), 1);
  }
  saved[submodel] = { reads, applied: kept.applied };
  write(docId, saved);
};

// ONE MATERIAL'S COLOUR IN A READING, BY HAND — a correction to the reading itself, kept with
// it; on the meshes at its next apply.
export const setReadingColour = (
  docId: string, submodel: string, id: string, material: string, rgb: [number, number, number],
) => {
  const one = (load(docId)[submodel]?.reads ?? []).find((read) => read.id === id);
  if (!one) return;
  keep(docId, submodel, id, {
    materials: one.materials.map((m) => (m.name === material ? { ...m, rgb } : m)),
  });
};

// ONE PART'S MATERIALS IN A READING, BY HAND — ticked into one (a tool: into several), or out of
// every one (none).
// A correction to the reading, kept with it and written onto the part when it is applied. Into
// its hardware half for a piece of hardware, its components' picks otherwise — started here on a
// reading that kept none, where it covers only the parts somebody has since changed.
export const setReadingPick = (
  docId: string, submodel: string, id: string, at: number, pairs: MaterialPair[], hardware: boolean,
  // Which part of a tool each is, by the material's words — the ones kept, where not said.
  of?: Record<string, string>,
) => {
  const one = (load(docId)[submodel]?.reads ?? []).find((read) => read.id === id);
  if (!one) return;
  if (hardware) {
    const was = one.hardware ?? { materials: [], asked: [], picks: [], reasoning: '', raw: '' };
    keep(docId, submodel, id, {
      hardware: {
        ...was,
        asked: was.asked.includes(at) ? was.asked : [...was.asked, at],
        picks: [...was.picks.filter((p) => p.at !== at), ...pairs.map((pair) => {
          const said = of ? of[surfaceKey(pair)]
            : was.picks.find((p) => p.at === at && surfaceKey(p.pair) === surfaceKey(pair))?.of;
          return { at, pair, ...(said ? { of: said } : {}) };
        })],
      },
    });
    return;
  }
  const was = one.partPicks ?? { asked: [], picks: {} };
  const picks = { ...was.picks };
  if (pairs[0]) picks[at] = pairs[0];
  else delete picks[at];
  keep(docId, submodel, id, {
    partPicks: { asked: was.asked.includes(at) ? was.asked : [...was.asked, at], picks },
  });
};

// THE HARDWARE'S HALF OF A PRESS, into the reading the components' half makes — whichever lands
// first: keep() makes the entry for the one that does.
export const keepHardware = (docId: string, submodel: string, id: string, hardware: HardwareReading) => {
  const exists = (load(docId)[submodel]?.reads ?? []).some((one) => one.id === id);
  keep(docId, submodel, id, { hardware, ...(exists ? {} : { at: new Date().toISOString() }) });
};

// A NEW READING'S ID, made by the press, which gives it to both calls.
// THE HARDWARE HALF OF A PRESS, APPLIED: filed under every colourway's reading of it, as the
// hardware is every colourway's (see keepHardware).
export const markHardwareApplied = (docId: string, id: string) => {
  for (const [submodel, kept] of Object.entries(load(docId))) {
    const one = kept.reads.find((read) => read.id === id);
    if (one?.hardware && !one.hardware.applied) {
      keep(docId, submodel, id, { hardware: { ...one.hardware, applied: true } });
    }
  }
};

export const newReadingId = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

// DROP ONE READING — never the applied one. That reading is also a fact about the bytes in
// every mesh of this colourway; with it gone the meshes would wear something no row names, and
// Phase 6 would close for want of an applied reading. The same rule the trim above keeps.
export const deleteReading = (docId: string, submodel: string, id: string) => {
  const saved = { ...load(docId) };
  const kept = saved[submodel];
  if (!kept || kept.applied === id) return;
  saved[submodel] = { ...kept, reads: kept.reads.filter((one) => one.id !== id) };
  write(docId, saved);
};

// A SET AND NOT A FLAG. Phase 5's submodel filter moves while a call is out, and a boolean
// would spin the button of whichever colourway was on screen rather than the one being read.
// AND THE MESHES BEING REWRITTEN FROM OUTSIDE A PRESS — a part's material followed onto them
// (see followPartMaterials), which is the same bytes changing and wants the same spinners.
export const markApplying = (
  docId: string, submodel: string, on: boolean, itemIds: number[] = [],
) => {
  if (on) store.patch(docId, { applyingItems: new Set(itemIds) });
  busy(docId, 'applying', submodel, on);
};

const busy = (
  docId: string, which: 'reading' | 'cropping' | 'applying', submodel: string, on: boolean,
) => {
  const next = new Set(store.getEntry(docId).snapshot[which]);
  if (on) next.add(submodel);
  else next.delete(submodel);
  store.patch(docId, which === 'reading' ? { reading: next }
    : which === 'cropping' ? { cropping: next } : { applying: next });
};

// WHAT THE TWO CALLS ARE SHOWN. The same photographs the 3D run would be shown, in the same
// order and with the angle the page knows each to be of — because the words change with them
// (see _picture_lines) and because a material read off a picture the build never sees is a
// colour nobody can check.
export type MaterialInput = { id: number; view?: string };

// WHICH PART MESH TAKES WHICH MATERIAL — Phase 2's answer, one pair per component part, by the
// catalog row the part IS.
//
// SENT BY THE PAGE, because the mapping is the page's: which row a part is comes out of the
// extraction's match or a live lookup by name (see partCatalogId), and it is on none of the
// rows this touches. A part is made of ONE thing however many slots its converter wrote, so
// the pair is a material and not a list.
export type PartPaint = { itemId: number; material: string };

// WHAT WAS WRITTEN ONTO ONE MESH FOR ONE MATERIAL — the map's size and the coordinates it was
// given. Reported by glb.textured, kept on the apply so the page can say what happened rather
// than only that something did.
export type Textured = {
  // The tile's real-world size in millimetres and how many pixels square it is, so "three
  // pixels to the millimetre" is a thing somebody can check.
  tileMm: number;
  side: number;
  contrast: number;
  // WHICH OF THE TWO SURFACES WAS DRAWN — the material's pattern: a grain stretched one way and
  // banded at its ring spacing, or a mottle with no direction at all.
  kind?: Pattern;
  anisotropy?: number;
  // The ring spacing a board was drawn at — the tile holds a whole number of them. Null for a
  // mottle, which has no rings in it.
  ringMm?: number | null;
  // How many periods across the tile is: its size over the period.
  repeats?: number;
  // How much of the mesh took coordinates, and how many triangles had to have their corners
  // split because they straddled two projections — see glb.textured.
  primitives: number;
  faces: number;
  vertices: number;
  split?: number;
  // What the factors were lifted by, the map being able only to darken.
  lift?: number;
  roughLift?: number;
  bytes?: number;
};

// THE PICTURE A MATERIAL'S NUMBERS DRAW, addressed rather than stored: the same measurement
// always draws the same tile (see materials_map, whose seed is a CRC of the name), so this is
// the very bytes the mesh was given and the browser caches it forever.
export const mapSrc = (
  name: string, pattern: Pattern, contrast: number, periodMm?: number, anisotropy?: number,
  chroma?: number, of: 'base' | 'rough' = 'base',
): string => apiUrl('/api/notice/3d/materials/map?' + new URLSearchParams({
  name,
  pattern,
  contrast: String(contrast),
  period: String(periodMm ?? 40),
  aniso: String(anisotropy ?? 0),
  chroma: String(chroma ?? 0),
  of,
}).toString());

// WHAT THE MATERIALS CALL WILL SAY, asked of the server rather than assembled here — the same
// argument writtenPrompt makes: the prompt lives in backend/openai_blender.py and changing it
// is a deploy, so a copy in this file would be a second prompt that looks like the first one
// right up until it does not.
export async function materialsPrompt(
  subject?: string, model?: string, views?: string[], parts?: unknown[],
  // The listing's materials — what the call is told to judge.
  names?: string[],
  // Which part each close-up is, in the order they follow the photographs.
  shots?: number[],
): Promise<{ prompt: string; model: string }> {
  const { data } = await axios.get<{ prompt: string; model: string }>(
    '/api/notice/3d/materials/prompt',
    {
      params: {
        ...(subject ? { subject } : {}),
        ...(model ? { model } : {}),
        ...(views?.length ? { view: views.map((v) => v || '') } : {}),
        ...(parts?.length ? { parts: JSON.stringify(parts) } : {}),
        ...(names?.length ? { name: names } : {}),
        ...(shots?.length ? { shot: shots.map(String) } : {}),
      },
      // Repeated keys rather than one comma-joined value — `view=front&view=` is a list of
      // two on the server, and an empty one in the middle keeps the numbering honest.
      paramsSerializer: { indexes: null },
    },
  );
  return data;
}

// What the first call is sent beyond the pictures: the parts (the components, numbered by their
// order here), the listing's materials by their words, and where the answer about the parts goes.
type MaterialsOpts = {
  source?: string; subject?: string; effort?: string; parts?: unknown[]; names?: string[];
  // THE CLOSE-UPS: a part's crops, cut, as the call is shown them after the photographs.
  shots?: { part: number; image: string }[];
  // And where each came from, for the measurement (see runMeasure) — in the same order.
  closeUps?: CloseUp[];
  // {n: material name}, n from 1 into `parts` — turned by the caller, which knows which part each
  // number is, into the picks the reading keeps. Null for an answer that placed no part.
  onParts?: (picks: Record<string, string>) => PartPicks | null;
};

// READ THE MATERIALS — one OpenAI call, the first press of the first subphase.
async function runMaterials(
  docId: string,
  submodel: string,
  id: string,
  inputs: MaterialInput[],
  model: string,
  opts: MaterialsOpts = {},
): Promise<{ parts: Record<string, string>; patches: Record<string, [number, number, number, number]> }> {
  if (!inputs.length || store.getEntry(docId).snapshot.reading.has(submodel)) return { parts: {}, patches: {} };
  busy(docId, 'reading', submodel, true);
  try {
    const { data } = await axios.post<{
      materials: Material[]; parts?: Record<string, string>;
      patches?: Record<string, [number, number, number, number]>; reasoning?: string; said?: string;
    }>(
      '/api/notice/3d/materials', {
        source: opts.source || 'listing',
        imageIds: inputs.map((one) => one.id),
        imageViews: inputs.map((one) => one.view || ''),
        model,
        effort: opts.effort,
        subject: opts.subject,
        parts: opts.parts,
        names: opts.names,
        shots: opts.shots,
      },
    );
    // ITS OWN ENTRY IN THE LIST, with nothing inherited: the patches belong to the materials
    // they are of, and a rectangle carried over from another reading would be evidence for a
    // material this one may not even name. SAVED FIRST — the reading is what was paid for.
    //
    // WITH WHICH MATERIAL EACH PART IS, kept on the reading and put on the parts by its apply.
    const partPicks = opts.onParts?.(data.parts || {}) ?? undefined;
    keep(docId, submodel, id, {
      ...(partPicks ? { partPicks } : {}),
      materials: data.materials || [],
      crops: [],
      stats: [],
      model,
      reasoning: data.reasoning || '',
      said: data.said || '',
      at: new Date().toISOString(),
    });
    return { parts: data.parts || {}, patches: data.patches || {} };
  } finally {
    busy(docId, 'reading', submodel, false);
  }
}

// MEASURE EACH MATERIAL WHERE THE CALL SAID IT CAN BE — no model: the clean area it drew on each
// close-up, mapped back onto the photograph that close-up was cut from, and read there inside the
// part's own outline (see measure_materials). The material is the one the same answer put the
// part on.
async function runMeasure(
  docId: string, submodel: string, id: string, closeUps: CloseUp[],
  said: { parts: Record<string, string>; patches: Record<string, [number, number, number, number]> },
  source = 'listing',
): Promise<void> {
  const answer = (load(docId)[submodel]?.reads ?? []).find((one) => one.id === id);
  if (!answer?.materials.length) return;
  const shots = Object.entries(said.patches).flatMap(([i, [px0, py0, px1, py1]]) => {
    const c = closeUps[Number(i)];
    const material = c && said.parts[String(c.part)];
    if (!c || !material) return [];
    const [x0, y0, x1, y1] = c.cut;
    const w = x1 - x0;
    const h = y1 - y0;
    return [{
      material, part: c.label, partMm: c.partMm, imageId: c.imageId, outlines: c.outlines,
      bbox: [x0 + px0 * w, y0 + py0 * h, x0 + px1 * w, y0 + py1 * h],
    }];
  });
  busy(docId, 'cropping', submodel, true);
  try {
    const { data } = await axios.post<{ crops: TextureCrop[]; stats: TextureStat[] }>(
      '/api/notice/3d/materials/measure', { source, materials: answer.materials, shots },
    );
    // AND THE MEASUREMENT ONTO THE MATERIAL ITSELF, which is what a build is handed: the stats
    // are the evidence, and `texture` is the part of them glb.grained spends.
    const measured = answer.materials.map((one) => {
      const read = (data.stats || []).find((s) => s.material === one.name);
      if (!read) {
        const { texture: _gone, ...flat } = one;
        return flat;
      }
      const texture = {
        ...(read.contrast != null ? { contrast: read.contrast } : {}),
        ...(read.chroma != null ? { chroma: read.chroma } : {}),
        ...(read.anisotropy != null ? { anisotropy: read.anisotropy } : {}),
        ...(read.periodMm != null ? { periodMm: read.periodMm } : {}),
        ...(one.pattern ? { pattern: one.pattern } : {}),
      };
      return Object.keys(texture).length ? { ...one, texture } : one;
    });
    keep(docId, submodel, id, {
      materials: measured,
      crops: data.crops || [],
      stats: data.stats || [],
    });
  } finally {
    busy(docId, 'cropping', submodel, false);
  }
}

// WHEN THE PRESS BEGAN, or nothing — kept beside the two busy sets rather than inside either,
// because it spans both calls and a clock that restarted between them would be two clocks.
const mark = (
  docId: string, which: 'startedAt' | 'applyAt' | 'applyTook', submodel: string,
  at: number | null,
) => {
  const map = { ...store.getEntry(docId).snapshot[which] };
  if (at == null) delete map[submodel];
  else map[submodel] = at;
  store.patch(docId, which === 'startedAt' ? { startedAt: map }
    : which === 'applyAt' ? { applyAt: map } : { applyTook: map });
};

// THE BUTTON: name the materials, then find and measure a patch of each.
//
// ONE PRESS AND TWO CALLS, which is not a contradiction — they are two questions to two
// models and there is never a reason to want the first without the second. The patches are
// what the numbers are read off, and a material with no numbers builds flat; making that a
// second button made "read the materials" a press that half-answered and said so in a line
// nobody had asked a question with.
//
// IN ORDER, because the second is about the first: the patches call is told the material
// names, which is why it cannot be made until they exist. A failure in the second leaves the
// first standing — the materials are read, and pressing again asks both again.
export async function readMaterials(
  docId: string,
  submodel: string,
  inputs: MaterialInput[],
  model: string,
  opts: MaterialsOpts = {},
  // ITS ID, the press's: the hardware call files its half under it too (see keepHardware).
  id: string = newReadingId(),
): Promise<string> {
  mark(docId, 'startedAt', submodel, Date.now());
  try {
    const said = await runMaterials(docId, submodel, id, inputs, model, opts);
    await runMeasure(docId, submodel, id, opts.closeUps ?? [], said, opts.source);
    // AND NOT ONTO THE MESHES. A press produces a reading; putting it on ten files is a
    // second decision, and one somebody makes after LOOKING at what came back — which is the
    // whole point of keeping the readings side by side. See applyMaterials, behind its own
    // button.
  } finally {
    mark(docId, 'startedAt', submodel, null);
  }
  return id;
}

// PUT ONE READING ON EVERY MESH THIS SUBMODEL'S PRODUCT HAS, and mark it as the one they wear.
//
// EXACTLY ONE READING IS APPLIED, and applied means the bytes: a colour and a grain live in
// each GLB (baseColorFactor, COLOR_0), written when the mesh was built, so four meshes of one
// chair carried four private copies that disagreed. Selecting a reading rewrites all of them,
// which is why this is one click and not a preference plus a button.
//
// SELECTED FIRST, THEN APPLIED. A build launched while this is in flight should be built with
// the reading somebody just chose, not with the one it is replacing; and a reading whose apply
// fails halfway is still the one the page says is chosen, with the error beside it, rather than
// the page and the disk disagreeing silently about which.
export async function applyMaterials(
  docId: string, submodel: string, id: string, parts: PartPaint[] = [],
  // WHAT HAS TO HAPPEN TO THE MESHES FIRST, under the same spinner — the pieces of a product
  // moved onto the materials the reading gives its parts (see movePieces).
  first?: () => Promise<unknown>,
): Promise<{ applied: number; skipped: { jobId: number; why?: string }[] }> {
  const kept = load(docId)[submodel];
  const one = (kept?.reads ?? []).find((read) => read.id === id);
  if (!one?.materials.length) return { applied: 0, skipped: [] };
  const saved = { ...load(docId) };
  saved[submodel] = { reads: kept?.reads ?? [one], applied: id };
  write(docId, saved);

  markApplying(docId, submodel, true, parts.map((p) => p.itemId));
  const began = Date.now();
  mark(docId, 'applyAt', submodel, began);
  mark(docId, 'applyTook', submodel, null);
  try {
    await first?.();
    const { data } = await axios.post<{ stamp: number; painted: number[] }>(
      '/api/notice/3d/materials/apply', { docId, submodel, materials: one.materials, parts },
    );
    // THE COLOURWAY'S PAINT MOVED, and with it the address of every shape drawn in it (see
    // colourwayPaint) — the server paints them in it, ahead. And the product's list is read
    // again, the signal both readers of it share (see bumpProductRuns).
    setColourwayStamp(submodel, data.stamp);
    bumpProductRuns(docId);
    mark(docId, 'applyTook', submodel, (Date.now() - began) / 1000);
    return { applied: (data.painted || []).length, skipped: [] };
  } finally {
    busy(docId, 'applying', submodel, false);
    mark(docId, 'applyAt', submodel, null);
  }
}
